import React, {ReactElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Button, Space, Table} from "antd";
import {SmartTableDataHandler} from "./smart-table.data-handler";
import {Pagination} from "../../../models/core/paginated.result";
import {LoadingSpin} from "../loading-spin/loading.spin";
import {useTranslate} from "../../../hooks/translate.hook";
import {SmartAction, SmartColumn, SmartFilterGroup, SmartFilterGroupValue} from "./smart-table.model";
import {
    antFiltersToSmartFilters, parseSmartFilters,
    smartColumnsToAntdColumns,
    smartColumnsToNameFieldConfigMap
} from "./smart-table.utils";
import {useURLParams} from "../../../hooks/url-params.hook";
import {FilterValues} from "../../../models/filter/filter.props";
import _ from "lodash";

const VIRTUAL_TABLE_THRESHOLD = 150;
const VIRTUAL_TABLE_MIN_HEIGHT = 240;
const VIRTUAL_TABLE_HEADER_HEIGHT = 55;
const VIRTUAL_TABLE_PAGINATION_HEIGHT = 64;
const VIRTUAL_TABLE_BOTTOM_GAP = 24;
const VIRTUAL_TABLE_COLUMN_WIDTH = 150;

interface VirtualTableScroll {
    x: number;
    y: number;
}

interface SmartTableProps<T> {
    handler: SmartTableDataHandler<T>
    columns: SmartColumn<T>[]
    actions?: SmartAction<T>[]
    extraFields?: Record<string, any>;
    filterGroups?: SmartFilterGroup[];
    setExtraFields?: (fields: Record<string, any>) => void;
    setDefaultExtraFields?: (fields: Record<string, any>) => void;
    loadOnlyIfExtraFieldsIsSet?: boolean;
    disabled?: boolean;
    disabledActions?: boolean;
    extraRight?: ReactElement;
}

export const SmartTable: React.FC<SmartTableProps<any>> = ({handler, columns, actions = [], disabled = false, disabledActions = false, extraFields, setExtraFields, setDefaultExtraFields, filterGroups, extraRight, loadOnlyIfExtraFieldsIsSet = false}) => {
    const t = useTranslate();

    const {urlParams, setURLParams} = useURLParams();

    const [customFilters, setCustomFilters] = useState<FilterValues | null>(null);

    const defaultFilters = useMemo(() => {
        if (null !== customFilters) {
            return customFilters;
        }
        return parseSmartFilters(String(urlParams.filters));
    }, [customFilters])

    const columnsNameFieldConfig = useMemo(() => smartColumnsToNameFieldConfigMap(columns), [columns])
    const stdColumns = useMemo(() => smartColumnsToAntdColumns(columns, columnsNameFieldConfig, defaultFilters, actions, disabledActions), [columns, defaultFilters, actions])

    const [items, setItems] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [fullLoading, setFullLoading] = useState<boolean>(false);

    const [pagination, setPagination] = useState<Pagination|null>(null);

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const generatedRowKeysRef = useRef(new WeakMap<object, React.Key>());
    const nextGeneratedRowKeyRef = useRef(0);
    const [measuredVirtualScroll, setMeasuredVirtualScroll] = useState<VirtualTableScroll | null>(null);

    const virtual = items.length > VIRTUAL_TABLE_THRESHOLD;
    const hasPagination = Boolean(pagination && pagination.totalPages > 0);

    const getRowKey = useCallback((record: any): React.Key => {
        if (record?.key !== undefined && record?.key !== null) {
            return record.key;
        }

        if (record?.id !== undefined && record?.id !== null) {
            return record.id;
        }

        if (record && typeof record === "object") {
            const existingKey = generatedRowKeysRef.current.get(record);
            if (existingKey !== undefined) {
                return existingKey;
            }

            const generatedKey = `smart-table-row-${++nextGeneratedRowKeyRef.current}`;
            generatedRowKeysRef.current.set(record, generatedKey);
            return generatedKey;
        }

        return String(record);
    }, []);

    useLayoutEffect(() => {
        if (!virtual) {
            setMeasuredVirtualScroll(null);
            return;
        }

        const updateVirtualScroll = () => {
            const container = tableContainerRef.current;
            if (!container) {
                return;
            }

            const rect = container.getBoundingClientRect();
            const paginationHeight = hasPagination ? VIRTUAL_TABLE_PAGINATION_HEIGHT : 0;
            const availableHeight = Math.floor(
                window.innerHeight
                - Math.max(rect.top, 0)
                - VIRTUAL_TABLE_HEADER_HEIGHT
                - paginationHeight
                - VIRTUAL_TABLE_BOTTOM_GAP
            );

            const nextScroll = {
                x: Math.max(Math.ceil(rect.width), stdColumns.length * VIRTUAL_TABLE_COLUMN_WIDTH),
                y: Math.max(VIRTUAL_TABLE_MIN_HEIGHT, availableHeight),
            };

            setMeasuredVirtualScroll(currentScroll => {
                if (currentScroll?.x === nextScroll.x && currentScroll.y === nextScroll.y) {
                    return currentScroll;
                }

                return nextScroll;
            });
        };

        updateVirtualScroll();
        const animationFrame = window.requestAnimationFrame(updateVirtualScroll);
        const resizeObserver = typeof ResizeObserver === "undefined"
            ? null
            : new ResizeObserver(updateVirtualScroll);

        if (tableContainerRef.current) {
            resizeObserver?.observe(tableContainerRef.current);
        }
        window.addEventListener("resize", updateVirtualScroll);

        return () => {
            window.cancelAnimationFrame(animationFrame);
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateVirtualScroll);
        };
    }, [hasPagination, items, stdColumns.length, virtual]);

    const virtualScroll = measuredVirtualScroll || {
        x: Math.max(window.innerWidth, stdColumns.length * VIRTUAL_TABLE_COLUMN_WIDTH),
        y: Math.max(VIRTUAL_TABLE_MIN_HEIGHT, Math.floor(window.innerHeight * 0.6)),
    };

    useEffect(() => {
        handler.setOnLoadingChange(loading => setLoading(loading))
        handler.setOnResult(({items, meta}) => {
            setItems(items);
            setPagination(meta);
            setURLParams(handler.toURLParams(), true)
        })

        handler.fromURLParams(urlParams);

        if (setDefaultExtraFields && handler.lastRequest) {
            setDefaultExtraFields(handler.lastRequest.extraFields);
        }

        if (!loadOnlyIfExtraFieldsIsSet && !disabled) {
            handler.reloadItemsWithLastRequest()
        }
    }, [handler])

    useEffect(() => {
        if (extraFields && !_.isEqual(handler.lastRequest?.extraFields, extraFields)) {
            handler.setExtraFields(extraFields);
            if (!disabled) {
                handler.reloadItemsWithLastRequest()
            }
        }
    }, [handler, extraFields, disabled])

    const applyFilterGroup = (value: SmartFilterGroupValue[]) => {
        handler.setFilters(value.map(k => ([k.name, k.value])))
        setCustomFilters((() => {
            const m: Record<string, any> = {};
            value.forEach(v => {
                m[v.name] = v.value;
            })
            return m;
        })());
        setFullLoading(true);
        setTimeout(() => {
            setFullLoading(false);
        }, 0);
    }

    if (disabled) {
        return <></>
    }

    if (!pagination || fullLoading) {
        return <LoadingSpin />
    }

    return <Space direction={"vertical"} size={[20, 20]} style={{width: "100%"}}>
        {filterGroups || extraRight ? (
            <Space direction={"horizontal"} style={{justifyContent: "space-between"}}>
                {filterGroups ? (
                    <Space direction={"horizontal"}>
                        {filterGroups.map(group => (
                            <Button onClick={() => applyFilterGroup(group.fields)}>
                                {t("FILTER-GROUP." + group.name.toUpperCase())}
                            </Button>
                        ))}
                    </Space>
                ) : null}
                {extraRight ? (
                    <>{extraRight}</>
                ) : null}
            </Space>
        ) : null}
        <div ref={tableContainerRef} style={{width: "100%"}}>
            <Table
                loading={loading}
                columns={stdColumns}
                dataSource={items}
                rowKey={getRowKey}
                virtual={virtual}
                scroll={virtual ? virtualScroll : {x: true}}
                onChange={(_, filters, sorter, extra) => {
                    switch (extra.action) {
                        case 'filter':
                            handler.setFilters(antFiltersToSmartFilters(filters, columnsNameFieldConfig));
                            setCustomFilters(null);
                            break;
                        case 'sort':
                            break;
                    }
                }}
                pagination={pagination.totalPages > 0 ? {
                    position: ["bottomLeft"],
                    total: pagination.total,
                    current: pagination.page,
                    pageSize: pagination.limit,
                    pageSizeOptions: ["10", "20", "30", "40", "50", "100"],
                    showSizeChanger: true,
                    showQuickJumper: true,
                    onChange: (page) => handler.setPage(page),
                    onShowSizeChange: (_, size) => handler.setLimit(size),
                    showTotal: (total) => t("TOTAL_ITEMS") + ": " + total,
                } : false}
            />
        </div>
    </Space>
}
